import { AlertTriangle, RefreshCw } from 'lucide-react';

interface PaneErrorFallbackProps {
  paneName: string;
  error?: Error;
  onRetry?: () => void;
}

/**
 * Error fallback UI displayed when a pane crashes.
 * Provides user-friendly error message and retry option.
 */
export const PaneErrorFallback = ({ paneName, error, onRetry }: PaneErrorFallbackProps) => {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      // Default: reload the page
      window.location.reload();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-8 text-center">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-red-100/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/3 w-48 h-48 bg-orange-100/20 rounded-full blur-3xl" />
      </div>

      <div className="relative">
        {/* Error icon */}
        <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-orange-50 rounded-2xl flex items-center justify-center shadow-lg mb-6 mx-auto">
          <AlertTriangle className="w-10 h-10 text-red-500" strokeWidth={1.5} />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-neutral-800 mb-2">
          {paneName} encountered an error
        </h2>

        {/* Description */}
        <p className="text-neutral-500 max-w-md mx-auto mb-2">
          Something went wrong while loading this section. The rest of the application is still working normally.
        </p>

        {/* Error details (development only) */}
        {error && process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-neutral-100 rounded-lg text-left max-w-lg mx-auto">
            <p className="text-xs font-mono text-red-600 break-all">
              {error.message}
            </p>
          </div>
        )}

        {/* Retry button */}
        <button
          onClick={handleRetry}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-coral-500 text-white font-medium rounded-xl hover:bg-coral-600 transition-colors shadow-sm"
        >
          <RefreshCw size={16} />
          <span>Reload Section</span>
        </button>

        {/* Status */}
        <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-neutral-200">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span className="text-xs font-medium text-neutral-600">Other sections are working</span>
        </div>
      </div>
    </div>
  );
};
